import React, { useState } from "react";
import { EyeIcon, EyeOffIcon, Spinner } from "./Shared/Icons";
import { FiAlertTriangle } from "react-icons/fi";
import { GoCheckCircleFill } from "react-icons/go";
import { usePasswordRecovery } from "../hooks/usePasswordRecovery";
import { ErrorAlert } from "../../../components/ErrorAlert";

interface RecoverViewProps {
  isDark: boolean;
  onCancel: () => void;
}

export const RecoverView: React.FC<RecoverViewProps> = ({ isDark, onCancel }) => {
  const {
    step,
    form,
    updateField,
    error,
    setError,
    successMessage,
    isLoading,
    handleSendCode,
    handleConfirmReset,
    resetState,
  } = usePasswordRecovery();

  const [showRecPass, setShowRecPass] = useState(false);
  
  const inputClass = "w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all bg-surface border-border text-text placeholder-text-dim focus:border-accent focus:bg-background";

  return (
    <div>
      <p className="text-xs text-center leading-relaxed mb-3 text-text-dim">
        {step === "SEND_CODE"
          ? "Ingresá tu correo electrónico para recibir un código de recuperación."
          : `Ingresá el código enviado a ${form.email} y tu nueva contraseña.`}
      </p>

      {error && (
        <ErrorAlert error={error} onDismiss={() => setError("")} className="mb-5" />
      )}

      {successMessage && (
        <div className="flex items-center gap-2.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl p-4 mb-5 text-xs font-semibold leading-normal">
          <GoCheckCircleFill style={{ color: "#10B981" }} size={18} className="flex-shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Form */}
      {step === "SEND_CODE" ? (
        <form onSubmit={handleSendCode} className="flex flex-col gap-4">
          <input
            type="email"
            placeholder="Correo electrónico"
            value={form.email}
            onChange={(e) => updateField("email", e.target.value)}
            disabled={isLoading}
            className={inputClass}
            autoFocus
          />

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 bg-accent hover:bg-accent/80 disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-accent/20 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Spinner />
                <span>Enviando código...</span>
              </>
            ) : (
              "Enviar código de recuperación"
            )}
          </button>

          <button
            type="button"
            onClick={onCancel}
            className="text-xs font-semibold text-center mt-2 cursor-pointer transition-all text-text-muted hover:text-accent"
          >
            Volver al inicio de sesión
          </button>
        </form>
      ) : (
        <form onSubmit={(e) => handleConfirmReset(e, onCancel)} className="flex flex-col gap-4">
          {/* Code */}
          <input
            type="text"
            placeholder="Código de verificación (6 dígitos)"
            value={form.code}
            onChange={(e) => updateField("code", e.target.value)}
            disabled={isLoading}
            className={inputClass}
            autoFocus
          />

          {/* Password */}
          <div className="relative">
            <input
              type={showRecPass ? "text" : "password"}
              placeholder="Nueva contraseña"
              value={form.newPassword}
              onChange={(e) => updateField("newPassword", e.target.value)}
              disabled={isLoading}
              className={inputClass}
            />
            <button
              type="button"
              onClick={() => setShowRecPass(!showRecPass)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent transition-all text-xs flex items-center justify-center"
            >
              {showRecPass ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>

          {/* Confirm Password */}
          <input
            type="password"
            placeholder="Confirmar contraseña"
            value={form.confirmPassword}
            onChange={(e) => updateField("confirmPassword", e.target.value)}
            disabled={isLoading}
            className={inputClass}
          />

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 bg-accent hover:bg-accent/80 disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-accent/20 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Spinner />
                <span>Restableciendo...</span>
              </>
            ) : (
              "Guardar nueva contraseña"
            )}
          </button>
{/* 
          <button
            type="button"
            onClick={handleResend}
            className="text-xs font-semibold text-center mt-2 cursor-pointer transition-all text-text-muted hover:text-accent"
          >
            Reenviar código
          </button> */}
        </form>
      )}
    </div>
  );
};
